import { Alert, Button, Card, Col, Row, message } from "antd";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { fetchBookableDate } from "../../../apis/This";
import {
  deleteUnableTime,
  fetchUnableTime,
  postUnableTime,
} from "../../../apis/Unbookable";
import useBeforeUnload from "../../../hooks/useBeforeUnload";
import useModals from "../../../hooks/useModals";
import useMouseDown from "../../../hooks/useMouseDown";
import useShop from "../../../hooks/useShop";
import { useWindowResize } from "../../../hooks/useWIndowResize";
import { RepairShopThisBookableInfo } from "../../../model/RepairShop/This/Bookable";
import { HttpError } from "../../../network/types";
import { BooksCalendar } from "./Calendar";
import { UnbookablePreview } from "./Preview";
import { SelectionTimeComponent } from "./SelectionTime";
import { CardTitleTypo } from "./index.styled";

interface UnbookableProps {}

/**
 * 예약불가
 * @TODO 예약가능일자 API 연동
 * @TODO 예약가능시간 목록 API 연동
 * @TODO 예약불가시간 세팅
 * @returns
 */
export const Unbookable: React.FC<UnbookableProps> = () => {
  const queryClient = useQueryClient();
  const { shop } = useShop();

  const { height } = useWindowResize();
  const { openConfirmModal, closeModal, openAlertModal } = useModals();

  const [selected, setSelected] = useState<moment.Moment>(); // 현재 선택일자

  const [selectedTimes, setSelectedTimes] = useState<
    moment.Moment[] | undefined
  >([]); // 전체 선택된 날짜정보

  const { data, isError, error } = useQuery(
    ["BookableDates", shop],
    fetchBookableDate,
    {
      onError: (err: HttpError) => {
        if (err?.status !== 500) {
          message.error(err.message);
        }
      },
    }
  );

  const {
    data: unableTimes,
    refetch,
    remove,
  } = useQuery(
    ["unableTimes", selected && selected.format("YYYYMMDD"), shop],
    () => fetchUnableTime(selected?.startOf("date").toISOString()),
    {
      enabled: !!selected,
      select: (data) =>
        data
          .filter(({ businessUnabledAt }) =>
            moment(businessUnabledAt).isSame(selected, "date")
          )
          .map(({ businessUnabledAt }) => moment(businessUnabledAt)),
    }
  );

  const { mutate, mutateAsync } = useMutation(postUnableTime, {
    onSuccess: () => {
      message.success("예약 불가시간이 설정되었습니다.");
      refetch();
    },
    onError: (err: any) => {
      if (err.status === 400) {
        openAlertModal({
          title: "예약불가 설정 실패",
          contents: (
            <div>
              {err.message.split("\n").map((text: string) => (
                <p>{text}</p>
              ))}
            </div>
          ),
        });
      } else {
        message.error(err.message);
      }
    },
  });

  const { mutate: deleteMutate, mutateAsync: deleteMutateAsync } = useMutation(
    deleteUnableTime,
    {
      onSuccess: () => {
        message.success("예약 불가시간이 설정되었습니다.");
        remove();
        refetch();
      },
      onError: (err: any) => {
        message.success("예약 불가시간이 설정되었습니다.");
        remove();
        refetch();
        console.log(err.message);
        /**
         * @notice 현재 삭제할 리스트가 존재하지 않는 경우에만 에러발생하여 일단 숨김
         * @history 230719
         * @author carol
         */
        // if (err.status === 400) {
        //   openAlertModal({
        //     title: "예약불가 설정 실패",
        //     contents: (
        //       <div>
        //         {err.message.split("\n").map((text: string) => (
        //           <p>{text}</p>
        //         ))}
        //       </div>
        //     ),
        //   });
        // } else {
        //   alertError(err.message);
        // }
      },
    }
  );

  /**
   * @TODO 예약불가 등록 API 쏘기
   */

  const handleChangeDate = (date: moment.Moment) => {
    const data = queryClient.getQueryData<RepairShopThisBookableInfo>([
      "BookableTimes",
      selected?.format("YYYYMMDD"),
      shop,
    ]);

    const unableTimeString =
      unableTimes?.map((time) => time.toISOString()) || [];

    const oldest =
      data?.bookableBusinessHourList.filter((hour) =>
        unableTimeString.includes(hour)
      ) || [];
    const newest = selectedTimes?.map((time) => time.toISOString()) || [];

    const diff = oldest
      .filter((old) => !newest.includes(old))
      .concat(newest.filter((time) => !oldest.includes(time)));

    if (diff.length === 0) {
      setSelected(date);
    } else {
      openConfirmModal({
        title: "변경사항 저장",
        contents: "변경사항을 저장하시겠습니까?",
        onCancel: () => {
          setSelected(date);
          closeModal();
        },
        onOk: () => {
          /**
           * @TODO 세이브 처리
           */
          if (selectedTimes && selectedTimes.length > 0) {
            const times = selectedTimes.map((time) => ({
              businessUnabledAt: time.toISOString(),
            }));
            mutateAsync({ unableTimes: [...times] }).then(() => {
              setSelected(date);
              closeModal();
            });
          } else {
            const deleteTimes =
              queryClient.getQueryData<RepairShopThisBookableInfo>([
                "BookableTimes",
                selected?.format("YYYYMMDD"),
                shop,
              ]);
            const times =
              deleteTimes?.bookableBusinessHourList.map((time) => ({
                businessUnabledAt: time,
              })) || [];
            deleteMutateAsync({ unableTimes: [...times] }).then(() => {
              setSelected(date);
              closeModal();
            });
          }
        },
      });
    }
  };

  const handleSave = () => {
    if (selectedTimes && selectedTimes.length > 0) {
      const times = selectedTimes.map((time) => ({
        businessUnabledAt: time?.toISOString(),
      }));
      mutate({ unableTimes: [...times] });
    } else {
      const deleteTimes = queryClient.getQueryData<RepairShopThisBookableInfo>([
        "BookableTimes",
        selected?.format("YYYYMMDD"),
        shop,
      ]);
      const times =
        deleteTimes?.bookableBusinessHourList.map((time) => ({
          businessUnabledAt: time,
        })) || [];
      deleteMutate({ unableTimes: [...times] });
    }
  };

  useEffect(() => {
    if (data && selected === undefined) {
      const date = moment(data.bookableDate[0]);
      setSelected(date);
    }
  }, [data]);

  useEffect(() => {
    if (selected) {
      setSelectedTimes([]);
    }
  }, [selected]);

  /**
   * clientheight - Header - tabs - margin(top + bottom) - subMenu - Menu - margin(bottom) - border (bottom; header + tab)
   */
  const layoutHeight = useMemo(
    () => height - 64 - 48 - 24 * 2 - 24 - 48 - 24 - 2,
    [height]
  );

  const enableDate = useMemo(
    () =>
      data?.bookableDate.find((time) => moment(time).isSame(selected, "date")),
    [selected, data]
  );

  const checkUnSaved = useCallback(() => {
    const data = queryClient.getQueryData<RepairShopThisBookableInfo>([
      "BookableTimes",
      selected?.format("YYYYMMDD"),
    ]);

    const unableTimeString =
      unableTimes?.map((time) => time.toISOString()) || [];

    const oldest =
      data?.bookableBusinessHourList.filter((hour) =>
        unableTimeString.includes(hour)
      ) || [];
    const newest = selectedTimes?.map((time) => time.toISOString()) || [];

    const diff = oldest
      .filter((old) => !newest.includes(old))
      .concat(newest.filter((time) => !oldest.includes(time)));

    return diff;
  }, [selected, unableTimes, selectedTimes]);

  const handleMousedown = useCallback(
    (e: MouseEvent) => {
      const open = (e.target as HTMLElement).getAttribute("open");
      const tab = (e.target as HTMLElement).closest("[data-node-key]");

      if ((open !== undefined && open !== null) || tab) {
        const diff = checkUnSaved();
        if (diff.length > 0) {
          openConfirmModal({
            title: "변경사항 저장",
            contents: "변경사항을 저장하시겠습니까?",
            onCancel: () => {
              closeModal();
              ((tab as HTMLElement) || (e.target as HTMLElement)).click();
            },
            onOk: () => {
              /**
               * @TODO 세이브 처리
               */
              if (selectedTimes && selectedTimes.length > 0) {
                const times = selectedTimes.map((time) => ({
                  businessUnabledAt: time.toISOString(),
                }));
                mutateAsync({ unableTimes: [...times] }).then(() => {
                  closeModal();
                  (e.target as HTMLElement).click();
                });
              } else {
                const deleteTimes =
                  queryClient.getQueryData<RepairShopThisBookableInfo>([
                    "BookableTimes",
                    selected?.format("YYYYMMDD"),
                    shop,
                  ]);
                const times =
                  deleteTimes?.bookableBusinessHourList.map((time) => ({
                    businessUnabledAt: time,
                  })) || [];
                deleteMutateAsync({ unableTimes: [...times] }).then(() => {
                  closeModal();
                  (e.target as HTMLElement).click();
                });
              }
            },
          });
        }
      }
    },
    [selected, selectedTimes, unableTimes]
  );

  const handleBeforeUnload = useCallback(
    (e: BeforeUnloadEvent) => {
      const diff = checkUnSaved();
      if (diff.length > 0) {
        e.returnValue = "변경사항을 저장하시겠습니까?";
        return openConfirmModal({
          title: "변경사항 저장",
          contents: "변경사항을 저장하시겠습니까?",
          onCancel: () => {
            closeModal();
          },
          onOk: () => {
            /**
             * @TODO 세이브 처리
             */
            if (selectedTimes && selectedTimes.length > 0) {
              const times = selectedTimes.map((time) => ({
                businessUnabledAt: time.toISOString(),
              }));
              mutateAsync({ unableTimes: [...times] }).then(() => {
                closeModal();
              });
            } else {
              const deleteTimes =
                queryClient.getQueryData<RepairShopThisBookableInfo>([
                  "BookableTimes",
                  selected?.format("YYYYMMDD"),
                  shop,
                ]);
              const times =
                deleteTimes?.bookableBusinessHourList.map((time) => ({
                  businessUnabledAt: time,
                })) || [];
              deleteMutateAsync({ unableTimes: [...times] }).then(() => {
                closeModal();
              });
            }
          },
        });
      }
      return true;
    },
    [selected, selectedTimes, unableTimes]
  );

  useMouseDown(handleMousedown, [selected, selectedTimes, unableTimes]);
  useBeforeUnload(handleBeforeUnload, [selected, selectedTimes, unableTimes]);

  if (isError && error?.status === 500)
    return (
      <Alert
        message="예약정보 설정을 완료하셔야 예약불가설정이 가능해요."
        type="warning"
        showIcon
      />
    );

  return (
    <Row
      gutter={16}
      justify="start"
      align="stretch"
      style={{
        flexWrap: "nowrap",
        minWidth: 736,
        height: layoutHeight,
        overflow: "auto",
      }}
    >
      <Col flex="0 0 736px">
        <Card
          style={{ height: "100%", display: "inline-flex" }}
          bordered={false}
        >
          <Card.Grid
            style={{ width: "50%", height: "61px", padding: "16px 24px" }}
            hoverable={false}
          >
            <CardTitleTypo>날짜 선택</CardTitleTypo>
          </Card.Grid>
          <Card.Grid
            style={{ width: "50%", height: "61px", padding: "16px 24px" }}
            hoverable={false}
          >
            <Row justify="space-between">
              <Col>
                <CardTitleTypo>{selected?.format("YYYY.MM.DD")}</CardTitleTypo>
              </Col>
              <Col>
                <Button type="primary" onClick={handleSave}>
                  저장
                </Button>
              </Col>
            </Row>
          </Card.Grid>
          <Card.Grid
            style={{
              width: "50%",
              height: "calc(100% - 61px)",
              overflow: "auto",
            }}
            hoverable={false}
          >
            <BooksCalendar
              current={selected}
              disableDates={data?.bookableDate.map((date) => moment(date))}
              onChange={handleChangeDate}
            />
          </Card.Grid>
          <Card.Grid
            style={{
              width: "50%",
              height: "calc(100% - 61px)",
              overflow: "auto",
            }}
            hoverable={false}
          >
            <SelectionTimeComponent
              date={selected}
              initValues={unableTimes}
              onChange={(times) => setSelectedTimes(times)}
              hidden={!enableDate}
            />
          </Card.Grid>
        </Card>
      </Col>
      <Col flex="0 0 400px">
        <Card
          style={{ height: "100%", width: "100%", overflow: "hidden" }}
          title={<CardTitleTypo>설정 미리보기</CardTitleTypo>}
          bordered={false}
          bodyStyle={{
            padding: 20,
            height: "calc(100% - 40px - 20px)",
            boxSizing: "border-box",
            overflow: "auto",
          }}
        >
          <UnbookablePreview
            current={selected}
            selectedTimes={selectedTimes}
            disableDates={data?.bookableDate.map((date) => moment(date))}
          />
        </Card>
      </Col>
    </Row>
  );
};
